import React from "react";
import { Box, Grid, Typography, Chip, Tooltip, Stack } from "@mui/material";
import { CSVLink } from "react-csv";
// import useProtocols from "data/hooks/useProtocols";
import MightyDataGrid from "../../../../components/organisms/MightyDataGrid";
import useDataGridActions from "../../../../hooks/useDataGridActions";
import useCoachConsoleDocumentIdsVisible from "../../../../hooks/useCoachConsoleDocumentIdsVisible";

const ExerciseDataGrid = ({
  exercises,
  muscleGroupsKey,
  equipmentKey,
  // allProtocols,
  onClick,
  onDelete,
  onRestore,
  onEdit,
  // New pagination props
  paginationMode = "client", // or "server" for server-side pagination
  loading = false,
  totalRows = 0,
  pageSize = 25,
  page = 0, // Current page for server-side pagination
  onPageChange,
  onPageSizeChange,
}) => {
  const showDocumentIds = useCoachConsoleDocumentIdsVisible();
  // const { findExercise } = useProtocols();
  const dataGridActions = useDataGridActions();

  // Create exercise actions configuration
  const createExerciseActions = (exercise) => {
    const actions = [];

    // Always add edit action
    actions.push(
      dataGridActions.createEditAction({
        onClick: (item) => onEdit(item.exercise),
        label: "Edit Exercise",
        eventData: {
          buttonName: "Edit Exercise",
          pageName: "Exercises",
        },
      }),
    );

    // Add delete action for custom exercises that aren't deleted
    if (exercise.isCustom && !exercise.isDeleted && onDelete) {
      actions.push(
        dataGridActions.createDeleteAction({
          onClick: (item) => onDelete(item.exercise),
          label: "Delete Exercise",
          title: "Delete this exercise?",
          message:
            "This action cannot be undone. The exercise will be permanently removed.",
          eventData: {
            buttonName: "Delete Exercise",
            pageName: "Exercises",
          },
          cancelEventData: {
            buttonName: "Cancel Exercise Delete",
            pageName: "Exercises",
          },
        }),
      );
    }

    // Add restore action for custom exercises that are deleted
    if (exercise.isCustom && exercise.isDeleted && onRestore) {
      actions.push({
        label: "Restore Exercise",
        onClick: (item) => onRestore(item.exercise),
        icon: "restore",
        eventData: {
          buttonName: "Restore Exercise",
          pageName: "Exercises",
        },
      });
    }

    return actions;
  };

  const csvData = React.useMemo(() => {
    if (!exercises) {
      return null;
    }
    const data = exercises.map((e) => {
      const row = {
        name: e.name,
        description: e.description,
        instructions: e.instructions,
        difficulty: e.difficulty,
        icon: e.icon,
        videos: e.videos ? "true" : "false",
        images: e.images ? "true" : "false",
      };
      if (showDocumentIds) {
        row.id = e.id;
      }
      return row;
    });
    const headers = [
      ...(showDocumentIds ? [{ label: "id", key: "id" }] : []),
      { label: "name", key: "name" },
      { label: "description", key: "description" },
      { label: "instructions", key: "instructions" },
      { label: "difficulty", key: "difficulty" },
      { label: "icon", key: "icon" },
      { label: "videos", key: "videos" },
      { label: "images", key: "images" },
    ];

    return {
      data: data,
      headers: headers,
      filename: "exercises.csv",
    };
  }, [exercises, showDocumentIds]);

  const columns = React.useMemo(() => {
    return [
      {
        field: "name",
        headerName: "Exercise Name",
        minWidth: 180,
        flex: 1,
        hideable: false,
        headerAlign: "left",
        align: "left",
      },
      {
        field: "description",
        headerName: "Description",
        minWidth: 200,
        flex: 1,
        headerAlign: "left",
        align: "left",
        renderCell: (params) => {
          const description = params.row.description;
          if (!description) {
            return (
              <Typography
                variant="body2"
                color="text.secondary"
                style={{ fontStyle: "italic" }}
              >
                No description
              </Typography>
            );
          }

          const truncatedDesc =
            description.length > 80
              ? description.substring(0, 80) + "..."
              : description;

          return (
            <Tooltip title={description} placement="top">
              <Typography variant="body2" style={{ fontSize: "0.875rem" }}>
                {truncatedDesc}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        field: "muscleGroups",
        headerName: "Muscle Groups",
        width: 160,
        headerAlign: "left",
        align: "left",
        renderCell: (params) => {
          const muscleGroups = params.row.muscleGroups || [];
          if (!muscleGroups || muscleGroups.length === 0) {
            return (
              <Typography
                variant="body2"
                color="text.secondary"
                style={{ fontStyle: "italic" }}
              >
                None
              </Typography>
            );
          }

          const muscleGroupNames = muscleGroups.join(", ");
          const truncated =
            muscleGroupNames.length > 25
              ? muscleGroupNames.substring(0, 25) + "..."
              : muscleGroupNames;

          return (
            <Tooltip title={muscleGroupNames}>
              <Typography variant="body2" style={{ fontSize: "0.875rem" }}>
                {truncated}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        field: "media",
        headerName: "Media",
        width: 80,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => {
          const hasVideos = params.row.videos === "Yes";
          const hasImages = params.row.images === "Yes";

          if (!hasVideos && !hasImages) {
            return (
              <Typography variant="body2" color="text.secondary">
                None
              </Typography>
            );
          }

          return (
            <Stack direction="row" spacing={0.5} alignItems="center">
              {hasVideos && (
                <Tooltip title="Has videos">
                  <Chip
                    label="V"
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </Tooltip>
              )}
              {hasImages && (
                <Tooltip title="Has images">
                  <Chip
                    label="I"
                    size="small"
                    color="secondary"
                    variant="outlined"
                  />
                </Tooltip>
              )}
            </Stack>
          );
        },
      },
      {
        field: "status",
        headerName: "Status",
        width: 100,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => {
          const isCustom = params.row.isCustom === "Yes";
          const isDeleted = params.row.isDeleted === "Yes";

          if (isDeleted) {
            return (
              <Chip
                label="Archived"
                size="small"
                color="error"
                variant="outlined"
              />
            );
          }

          return (
            <Chip
              label={isCustom ? "Custom" : "Built-in"}
              size="small"
              color={isCustom ? "secondary" : "default"}
              variant="outlined"
            />
          );
        },
      },
    ];
  }, []);

  const rows = React.useMemo(() => {
    if (!exercises || !muscleGroupsKey || !equipmentKey) {
      return [];
    }
    return exercises.map((exercise) => {
      console.log("exercise", exercise);

      // Handle both data structures:
      // 1. New structure: exercise.muscleGroups = ["abs", "abs"]
      // 2. Old structure: exercise.mainMuscleGroups = [id1, id2], exercise.secondaryMuscleGroups = [id3, id4]

      let muscleGroups = [];

      if (exercise.muscleGroups && Array.isArray(exercise.muscleGroups)) {
        // New structure - muscle groups are already strings
        muscleGroups = [...exercise.muscleGroups];
      } else {
        // Old structure - need to map IDs to names
        const mainGroups = exercise.mainMuscleGroups || [];
        const secondaryGroups = exercise.secondaryMuscleGroups || [];

        const mainNames = mainGroups
          .map((id) => muscleGroupsKey[id])
          .filter(Boolean);
        const secondaryNames = secondaryGroups
          .map((id) => muscleGroupsKey[id])
          .filter(Boolean);

        muscleGroups = [...mainNames, ...secondaryNames];
      }

      // Remove duplicates
      muscleGroups = [...new Set(muscleGroups)];

      return {
        id: exercise.id,
        exercise: exercise,
        name: exercise.name,
        description: exercise.description,
        instructions:
          exercise.instructions && exercise.instructions.length > 0
            ? "Yes"
            : "No",
        complexity: exercise.difficulty,
        isCustom: exercise.isCustom ? "Yes" : "No",
        isDeleted: exercise.isDeleted ? "Yes" : "No",
        muscleGroups: muscleGroups,
        mainMuscleGroups: exercise.mainMuscleGroups
          ? exercise.mainMuscleGroups
              .map((id) => muscleGroupsKey[id])
              .join(", ")
          : "",
        secondaryMuscleGroups: exercise.secondaryMuscleGroups
          ? exercise.secondaryMuscleGroups
              .map((id) => muscleGroupsKey[id])
              .join(", ")
          : "",
        equipment: exercise.equipment
          ? exercise.equipment.map((id) => equipmentKey[id]).join(", ")
          : "",
        videos: exercise.videos && exercise.videos.length > 0 ? "Yes" : "No",
        images:
          (exercise.images &&
            exercise.images.length > 0 &&
            exercise.images?.[0] &&
            exercise.images?.[0].length > 0) ||
          (exercise.images?.[1] && exercise.images?.[1].length > 0)
            ? "Yes"
            : "No",
      };
    });
  }, [exercises, muscleGroupsKey, equipmentKey]);

  console.log("rows", rows);
  console.log("🔄 ExerciseDataGrid loading state:", { loading });
  console.log("🔄 ExerciseDataGrid combined loading details:", { 
    mainLoading: loading, 
    deleteLoading: onDelete ? "onDelete function provided" : "no onDelete function"
  });

  return (
    <>
      <Box sx={{ position: "relative", width: "100%", minHeight: "400px" }}>
        <MightyDataGrid
          entityName="exercises"
          rows={rows}
          columns={columns}
          loading={loading}
          onRowClick={(row) => onClick(row.exercise)}
          showActions={true}
          actions={(row) => createExerciseActions(row.exercise)}
          pagination={
            paginationMode === "client" || paginationMode === "server"
          }
          paginationMode={paginationMode}
          totalRows={totalRows}
          pageSize={pageSize}
          page={page}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          pageSizeOptions={[5, 10, 25, 50]}
          viewportOffset={500} // Adjust based on your header height
        />

        {/* Loading overlay for when data is being refreshed */}
        {loading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 9999,
              borderRadius: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
                backgroundColor: "background.paper",
                padding: 3,
                borderRadius: 2,
                boxShadow: 3,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  border: "4px solid",
                  borderColor: "primary.light",
                  borderTopColor: "primary.main",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                  "@keyframes spin": {
                    "0%": {
                      transform: "rotate(0deg)",
                    },
                    "100%": {
                      transform: "rotate(360deg)",
                    },
                  },
                }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontWeight: "medium" }}
              >
                Loading exercises...
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      {csvData && (
        <Grid container justifyContent="center" marginTop={2}>
          <Box>
            <CSVLink {...csvData}>Export to CSV</CSVLink>
          </Box>
        </Grid>
      )}
    </>
  );
};

export default ExerciseDataGrid;